import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneShell from '../components/shared/PhoneShell';
import BackHeader from '../components/shared/BackHeader';
import SportFilter from '../components/shared/SportFilter';
import { toggleBetSlip } from '../utils/betSlip';
import { useLang } from '../i18n';

const API = process.env.REACT_APP_API_BASE_URL;
const POLL_MS = 30_000;

const css = `
  .fb-leagues { display: flex; gap: 8px; padding: 10px 14px 8px; overflow-x: auto; flex-shrink: 0; }
  .fb-leagues::-webkit-scrollbar { display: none; }
  .fb-league-btn { display: flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 20px; border: 1px solid rgba(255,255,255,.08); background: rgba(255,255,255,.03); color: #a0a8b8; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: all .2s; flex-shrink: 0; }
  .fb-league-btn.active { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.1); color: #00d4aa; }
  .fb-league-btn img { width: 16px; height: 16px; object-fit: contain; border-radius: 2px; }
  .fb-league-btn .fb-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #ff3b3b; flex-shrink: 0; }

  .fb-list { padding: 6px 14px 80px; }

  .fb-card { background: rgba(255,255,255,.02); border: 1px solid rgba(255,255,255,.06); border-radius: 14px; margin-bottom: 10px; overflow: hidden; cursor: pointer; transition: border-color .2s; }
  .fb-card:active { border-color: rgba(0,212,170,.3); }

  .fb-card-head { display: flex; align-items: center; justify-content: space-between; padding: 7px 12px; border-bottom: 1px solid rgba(255,255,255,.04); gap: 6px; }
  .fb-card-league { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
  .fb-card-league img { width: 14px; height: 14px; object-fit: contain; flex-shrink: 0; }
  .fb-card-league-name { font-size: 11px; color: #5a5972; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .fb-card-date { font-size: 11px; color: #5a5972; flex-shrink: 0; }
  .fb-card-live-badge { font-size: 10px; font-weight: 800; color: #ff3b3b; background: rgba(255,59,59,.12); padding: 2px 7px; border-radius: 10px; flex-shrink: 0; }

  .fb-card-body { display: flex; align-items: center; padding: 10px 12px; gap: 10px; }
  .fb-teams { flex: 1; display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .fb-team { display: flex; align-items: center; gap: 8px; }
  .fb-team img { width: 20px; height: 20px; object-fit: contain; flex-shrink: 0; }
  .fb-team-name { font-size: 13px; font-weight: 600; color: #e4e7ec; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .fb-score { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; flex-shrink: 0; padding: 0 4px; }
  .fb-score-val { font-size: 15px; font-weight: 800; color: #fff; line-height: 1; }

  .fb-odds { display: flex; gap: 5px; flex-shrink: 0; }
  .fb-odd-btn { display: flex; flex-direction: column; align-items: center; padding: 7px 8px; min-width: 42px; border-radius: 8px; border: 1px solid rgba(255,255,255,.07); background: rgba(255,255,255,.03); cursor: pointer; transition: all .15s; }
  .fb-odd-btn.selected { border-color: rgba(0,212,170,.6); background: rgba(0,212,170,.12); }
  .fb-odd-btn:active { opacity: .7; }
  .fb-odd-label { font-size: 10px; color: #5a5972; font-weight: 700; margin-bottom: 2px; }
  .fb-odd-value { font-size: 12px; color: #e4e7ec; font-weight: 700; }
  .fb-odd-btn.selected .fb-odd-label, .fb-odd-btn.selected .fb-odd-value { color: #00d4aa; }
  .fb-no-odds { font-size: 11px; color: #3a3857; padding: 8px 0; }

  .fb-spinner { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; gap: 14px; }
  .fb-spinner-ring { width: 36px; height: 36px; border: 3px solid rgba(255,255,255,.06); border-top-color: #00d4aa; border-radius: 50%; animation: fb-spin 1s linear infinite; }
  @keyframes fb-spin { to { transform: rotate(360deg); } }
  .fb-spinner p { color: #5a5972; font-size: 13px; margin: 0; }
  .fb-empty { display: flex; align-items: center; justify-content: center; padding: 60px 20px; }
  .fb-empty p { color: #3a3857; font-size: 13px; }

  .fb-live-count { color: #ff3b3b; font-size: 11px; font-weight: 800; margin-left: 2px; }
  .fb-live-dot-sm { width: 6px; height: 6px; border-radius: 50%; background: #ff3b3b; display: inline-block; margin-right: 4px; animation: livePulse 1s ease-in-out infinite; }
  @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:.3} }
`;

function getParisDateTime() {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(new Date());
  const g = t => p.find(x => x.type === t)?.value || '00';
  return { date: `${g('year')}-${g('month')}-${g('day')}`, sec: parseInt(g('hour')) * 3600 + parseInt(g('minute')) * 60 + parseInt(g('second')) };
}
function timeSec(s) { if (!s) return 0; const [h, m, sec] = s.split(':').map(Number); return h * 3600 + m * 60 + (sec || 0); }
function isMatchLive(match) {
  if (match.status === 'finished') return false;
  if (match.status === 'live') return true;
  if (!match.livestart) return false;
  const { date: today, sec: nowSec } = getParisDateTime();
  const matchDate = match.date ? match.date.split('T')[0] : null;
  if (matchDate && matchDate !== today) return false;
  const start = timeSec(match.livestart);
  const end = match.endTime ? timeSec(match.endTime) : start + 7200;
  return nowSec >= start && nowSec < end;
}

function parseInternalOdds(match) {
  const mr = match.odds?.matchResult;
  if (!mr) return [];
  return [
    { label: '1', betType: 'Match Winner: Home', value: parseFloat(mr.teamA).toFixed(2), index: 0 },
    { label: 'X', betType: 'Match Winner: Draw', value: parseFloat(mr.draw).toFixed(2),  index: 1 },
    { label: '2', betType: 'Match Winner: Away', value: parseFloat(mr.teamB).toFixed(2), index: 2 },
  ].filter(o => !isNaN(parseFloat(o.value)));
}

function parseExternalOdds(fixture) {
  const mw = [];
  if (fixture.odds?.match_winner?.length > 0) {
    fixture.odds.match_winner.forEach((o, i) => {
      const map = {
        Home: { label: '1', betType: 'Match Winner: Home', index: i },
        Draw: { label: 'X', betType: 'Match Winner: Draw', index: i + 3 },
        Away: { label: '2', betType: 'Match Winner: Away', index: i + 6 },
      };
      const m = map[o.label];
      if (m) mw.push({ ...m, value: o.odd });
    });
  }
  return mw;
}

function getMinute(status) {
  if (!status) return null;
  const { short, elapsed } = status;
  if (short === 'HT') return 'MT';
  if (elapsed) return `${elapsed}'`;
  return null;
}

export default function LivePage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selBets, setSelBets] = useState([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const sync = () => setSelBets(JSON.parse(localStorage.getItem('betSlipItems') || '[]').map(b => b.id));
    sync();
    window.addEventListener('betAdded', sync);
    return () => window.removeEventListener('betAdded', sync);
  }, []);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [extRes, intRes] = await Promise.all([
        fetch(`${API}/api-bet/football/live.php`).then(r => r.json()).catch(() => null),
        fetch(`${API}/matchs/read_all.php`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({})
        }).then(r => r.json()).catch(() => null),
      ]);

      const extMap = new Map();
      if (extRes?.success && Array.isArray(extRes.fixtures)) {
        for (const f of extRes.fixtures) {
          const id = `ext:${f.league?.id || 'other'}`;
          if (!extMap.has(id)) {
            extMap.set(id, { id, name: f.league?.name || 'Football', logo: f.league?.logo, internal: false, matches: [] });
          }
          extMap.get(id).matches.push(f);
        }
      }

      const rawInternal = intRes?.matches || intRes?.championships?.flatMap(c => c.matches || []) || [];
      const intMap = new Map();
      for (const m of rawInternal.filter(isMatchLive)) {
        const name = m.league?.name || m.league || m.championship || 'Venezuela D2';
        const id = `internal:${name}`;
        if (!intMap.has(id)) {
          intMap.set(id, { id, name, logo: null, internal: true, matches: [] });
        }
        intMap.get(id).matches.push(m);
      }

      setLeagues([...Array.from(extMap.values()), ...Array.from(intMap.values())]);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    timerRef.current = setInterval(() => fetchAll(true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchAll]);

  const handleOdd = (e, fixture, betType, oddLabel, oddValue, oddIndex) => {
    e.stopPropagation();
    const [market] = betType.split(':');
    toggleBetSlip({
      id: `${fixture.fixture.id}-${oddIndex}`,
      matchId: fixture.fixture.id,
      fixtureId: fixture.fixture.id,
      match: `${fixture.teams.home.name} vs ${fixture.teams.away.name}`,
      homeTeam: fixture.teams.home.name,
      awayTeam: fixture.teams.away.name,
      date: new Date(fixture.fixture.date).toLocaleString('fr-FR'),
      fixtureDate: fixture.fixture.date,
      selection: betType,
      betType: market.trim(),
      betLabel: oddLabel,
      handicap: null,
      odds: parseFloat(oddValue),
      league: fixture.league?.name,
      leagueId: fixture.league?.id,
      isLive: true,
      sport: 'soccer',
    });
  };

  const handleInternalOdd = (e, match, betType, oddValue, oddIndex, leagueName) => {
    e.stopPropagation();
    const [market] = betType.split(':');
    const label = betType.includes('Home') ? '1' : betType.includes('Draw') ? 'X' : '2';
    toggleBetSlip({
      id: `live-${match.id}-match-${oddIndex}`,
      matchId: match.id,
      match: `${match.teamA?.name || '?'} vs ${match.teamB?.name || '?'}`,
      homeTeam: match.teamA?.name || '?',
      awayTeam: match.teamB?.name || '?',
      date: match.date ? new Date(match.date).toLocaleDateString('fr-FR') : '',
      fixtureDate: match.date || null,
      selection: betType,
      betType: market.trim(),
      betLabel: label,
      handicap: null,
      odds: parseFloat(oddValue),
      league: leagueName,
      isLive: true,
      sport: 'football',
    });
  };

  const displayLeagues = selectedId ? leagues.filter(l => l.id === selectedId) : leagues;
  const totalLive = leagues.reduce((s, l) => s + l.matches.length, 0);

  return (
    <PhoneShell activeTab="sports">
      <style>{css}</style>
      <BackHeader title={t('home.status.live')} />
      <SportFilter />

      {/* League tabs */}
      <div className="fb-leagues">
        <button
          className={'fb-league-btn' + (!selectedId ? ' active' : '')}
          onClick={() => setSelectedId(null)}
        >
          <span className="fb-live-dot-sm" />
          {t('common.all')}
          {totalLive > 0 && <span className="fb-live-count">{totalLive}</span>}
        </button>
        {leagues.map(l => (
          <button
            key={l.id}
            className={'fb-league-btn' + (selectedId === l.id ? ' active' : '')}
            onClick={() => setSelectedId(l.id)}
          >
            {l.internal
              ? <span className="fb-live-dot" />
              : l.logo && <img src={l.logo} alt="" />
            }
            {l.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="fb-spinner"><div className="fb-spinner-ring" /><p>{t('sports.loading')}</p></div>
      ) : totalLive === 0 ? (
        <div className="fb-empty"><p>{t('calendar.noLive') || 'Aucun match en direct'}</p></div>
      ) : (
        <div className="fb-list">
          {displayLeagues.map(league =>
            league.internal
              ? league.matches.map(match => {
                  const odds = parseInternalOdds(match);
                  return (
                    <div key={match.id} className="fb-card" onClick={() => navigate(`/live/${match.id}`)}>
                      <div className="fb-card-head">
                        <div className="fb-card-league">
                          <span className="fb-live-dot" />
                          <span className="fb-card-league-name">{league.name}</span>
                        </div>
                        <span className="fb-card-live-badge">LIVE</span>
                      </div>
                      <div className="fb-card-body">
                        <div className="fb-teams">
                          <div className="fb-team">
                            {match.teamA?.logo && <img src={match.teamA.logo} alt="" />}
                            <span className="fb-team-name">{match.teamA?.name || '?'}</span>
                          </div>
                          <div className="fb-team">
                            {match.teamB?.logo && <img src={match.teamB.logo} alt="" />}
                            <span className="fb-team-name">{match.teamB?.name || '?'}</span>
                          </div>
                        </div>
                        <div className="fb-score">
                          <span className="fb-score-val">{match.teamA?.score ?? 0}</span>
                          <span className="fb-score-val">{match.teamB?.score ?? 0}</span>
                        </div>
                        {odds.length > 0 ? (
                          <div className="fb-odds" onClick={e => e.stopPropagation()}>
                            {odds.map(o => {
                              const betId = `live-${match.id}-match-${o.index}`;
                              return (
                                <button
                                  key={o.index}
                                  className={'fb-odd-btn' + (selBets.includes(betId) ? ' selected' : '')}
                                  onClick={e => handleInternalOdd(e, match, o.betType, o.value, o.index, league.name)}
                                >
                                  <span className="fb-odd-label">{o.label}</span>
                                  <span className="fb-odd-value">{o.value}</span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="fb-no-odds">N/A</span>
                        )}
                      </div>
                    </div>
                  );
                })
              : league.matches.map(fixture => {
                  const odds = parseExternalOdds(fixture);
                  const minute = getMinute(fixture.fixture?.status);
                  return (
                    <div key={fixture.fixture.id} className="fb-card" onClick={() => navigate(`/match/football/${fixture.fixture.id}`)}>
                      <div className="fb-card-head">
                        <div className="fb-card-league">
                          {league.logo && <img src={league.logo} alt="" />}
                          <span className="fb-card-league-name">{league.name}</span>
                        </div>
                        <span className="fb-card-live-badge">LIVE</span>
                        {minute && <span className="fb-card-date">{minute}</span>}
                      </div>
                      <div className="fb-card-body">
                        <div className="fb-teams">
                          <div className="fb-team">
                            {fixture.teams?.home?.logo && <img src={fixture.teams.home.logo} alt="" />}
                            <span className="fb-team-name">{fixture.teams?.home?.name}</span>
                          </div>
                          <div className="fb-team">
                            {fixture.teams?.away?.logo && <img src={fixture.teams.away.logo} alt="" />}
                            <span className="fb-team-name">{fixture.teams?.away?.name}</span>
                          </div>
                        </div>
                        <div className="fb-score">
                          <span className="fb-score-val">{fixture.goals?.home ?? 0}</span>
                          <span className="fb-score-val">{fixture.goals?.away ?? 0}</span>
                        </div>
                        {odds.length > 0 ? (
                          <div className="fb-odds" onClick={e => e.stopPropagation()}>
                            {odds.map(o => {
                              const id = `${fixture.fixture.id}-${o.index}`;
                              return (
                                <button
                                  key={o.index}
                                  className={'fb-odd-btn' + (selBets.includes(id) ? ' selected' : '')}
                                  onClick={e => handleOdd(e, fixture, o.betType, o.label, o.value, o.index)}
                                >
                                  <span className="fb-odd-label">{o.label}</span>
                                  <span className="fb-odd-value">{o.value}</span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="fb-no-odds">N/A</span>
                        )}
                      </div>
                    </div>
                  );
                })
          )}
        </div>
      )}
    </PhoneShell>
  );
}
